import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReorderLevel } from 'src/app/models/ReorderLevel';
import { ReorderLevelService } from 'src/app/services/reorder-levels/reorder-level.service';
import { MakeService } from 'src/app/services/makes/make.service';
import { ModelService } from 'src/app/services/models/model.service';
import { GradeService } from 'src/app/services/grades/grade.service';
import { FuelTypesService } from 'src/app/services/fuel-types/fuel-types.service';
import { ApiResponse } from 'src/app/models/IApiResponse';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-reorder-levels',
  templateUrl: './reorder-levels.component.html',
  styleUrls: ['./reorder-levels.component.css']
})
export class ReorderLevelsComponent implements OnInit{
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  page: number = 1;
  count: number = 0;
  searchText: string = '';
  reorderLevels: ReorderLevel[] = [];
  form: FormGroup;
  isEditing = false;
  submitted: boolean = false;
  isProcessing: boolean = false;
  makes: any[] = [];
  models: any[] = [];
  grades: any[] = [];
  fuel_types: any[] = [];

  constructor(
    private reorderLevelService: ReorderLevelService,
    private fb: FormBuilder,
    private makeService:MakeService,
    private modelService:ModelService,
    private gradeService:GradeService,
    private fuelTypesService: FuelTypesService,
  ) {
    this.form = this.fb.group({
      id: [],
      make_id: ['', Validators.required],
      model_id: ['', Validators.required],
      grade_id: [null],
      fuel_type_id: [null],
      reorder_level: [0, [Validators.required, Validators.min(0)]]
    });
  }

  ngOnInit() {
    this.isProcessing =true;

    this.fetchVehicleMakes();
  }

  fetchVehicleMakes():void {
    this.makeService.getAllMakes().subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.makes = response.data
          this.isProcessing=false;
          this.getFuelTypes();
        }else{
          this.errorMessage=response.message;
          this.isProcessing=false;
          this.getFuelTypes();
        }
      },
      error: (error) => {
        this.errorMessage=error;
        this.isProcessing=false;
        this.getFuelTypes();
      }
    });
  }

  fetchVehicleModels(makeId:string):void {
    this.isProcessing =true;

    this.modelService.getModelsByMake(makeId).subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.models = response.data
          this.isProcessing=false;
        }else{
          this.errorMessage=response.message;
          this.isProcessing=false;
        }
      },
      error: (error) => {
        this.errorMessage=error;
        this.isProcessing=false;
      }
    });
  }

  onSelectChange(target: EventTarget|null) {
    if (target instanceof HTMLSelectElement) {

      const makeId = target.value;

      if(makeId!=='' && makeId!==undefined){
        this.fetchVehicleModels(makeId);
      }else{
        this.errorMessage="Invalid make selected!";
      }
    }
  }

  //Load models & grades
  onSelectModelChange(target: EventTarget|null) {
    if (target instanceof HTMLSelectElement) {
      const modelID = target.value;

      if(modelID!=='' && modelID!==undefined){
        this.getVehicleGrades(modelID);
      }else{
        this.errorMessage="Invalid model selected!";
      }
    }
  }

  /**
   * Get vehicle grades associated with
   * the selected model
   * @param modelID
   */
  getVehicleGrades(modelID:string): void {
    this.gradeService.getGradesByModel(modelID).subscribe((response: ApiResponse)=>this.grades=response.data);
  }

  /**
   * Load fuel types
   */
  getFuelTypes(): void {
    this.isProcessing =true;

    this.fuelTypesService.getAll().subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.fuel_types = response.data
          this.isProcessing=false;
          this.fetchReorderLevels();
        }else{
          this.errorMessage=response.message;
          this.isProcessing=false;
          this.fetchReorderLevels();
        }
      },
      error: (error) => {
        this.errorMessage=error;
        this.isProcessing=false;
        this.fetchReorderLevels();
      }
    });
  }

  /**
   * Load reorder levels
   */
  fetchReorderLevels() {
    this.isProcessing = true;

    this.reorderLevelService.getAll().subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.reorderLevels = response.data
          this.isProcessing=false;
        }else{
          this.errorMessage=response.message;
          this.isProcessing=false;
        }
      },
      error: (error) => {
        this.errorMessage=error;
        this.isProcessing=false;
      }
    });
  }

  submit() {
    this.submitted=true;
    this.isProcessing = true;

    if (this.form.value.id) {
      this.reorderLevelService.update(this.form.value.id, this.form.value).subscribe({
        next: (response) => {
          if(response.status==='success'){
            this.successMessage=response.message;
            this.errorMessage=''; //Clear error message incase it's available
            this.fetchReorderLevels();
            this.form.reset();
            this.submitted=false;
            this.isProcessing = false;
            this.isEditing = false;
          }else{
            this.errorMessage=response.message;
            this.isProcessing=false;
            this.submitted=false;
          }
        },
        error: (error) => {
          this.errorMessage=error;
          this.isProcessing=false;
          this.submitted=false;
        }
      });
    } else {
      this.reorderLevelService.create(this.form.value).subscribe({
        next: (response) => {
          if(response.status==='success'){
            this.successMessage=response.message;
            this.errorMessage=''; //Clear error message incase it's available
            this.fetchReorderLevels();
            this.form.reset();
            this.submitted=false;
            this.isProcessing = false;
          }else{
            this.errorMessage=response.message;
            this.submitted=false;
            this.isProcessing=false;
          }
        },
        error: (error) => {
          this.errorMessage=error;
          this.isProcessing=false;
          this.submitted=false;
        }
      });
    }
  }

  edit(item: ReorderLevel) {
    this.fetchVehicleModels(item.make_id.toString());
    this.getVehicleGrades(item.model_id.toString());
    this.form.patchValue(item);
    this.isEditing = true;
  }

  delete(event: Event,id: number) {
    event.preventDefault();
    this.confirmDelete(id);
  }

  confirmDelete(id: number){
      Swal.fire({
        title: 'Confirm Delete',
        text: 'Are you sure you want to delete this record? The action is irreversible!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes!',
        cancelButtonText: 'No'
      }).then((result) => {
        if (result.value) {
          this.isProcessing=true;

          this.reorderLevelService.delete(id)
          .subscribe({
            next: (response) => {
              if(response.status==='success'){
                this.isProcessing=false;
                this.successMessage=response.message;
                this.errorMessage=''; //Clear error message incase it's available

                //Reload reorder levels
                this.fetchReorderLevels();
              }else if(response.status==='error'){
                this.isProcessing=false;
                this.errorMessage=response.message;
              }else{
                this.isProcessing=false;
                this.errorMessage=response.message;
              }
            },
            error: (error) => {
              this.isProcessing=false;
              this.errorMessage=error;
            }
          });
      } else if (result.dismiss === Swal.DismissReason.cancel) {

      }
    })
  }

  cancel() {
    this.form.reset();
    this.isEditing = false;
  }

  pageChangeEvent(event: number){
    this.page = event;
    this.fetchReorderLevels();
  }

  onSearchTextChange(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value;
    this.page = 1; // Reset to page 1 after applying filter
  }
}
